import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService, AuthStatus } from '../services/authService';

interface RequireAuthProps {
  children: React.ReactElement;
  requiredRole?: 'Admin' | 'Operator';
}

/**
 * Protected route wrapper that requires authentication
 * Redirects to /login if not authenticated
 */
export const RequireAuth: React.FC<RequireAuthProps> = ({ children, requiredRole }) => {
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const status = await authService.getStatus();
      setAuthStatus(status);
    } catch (error) {
      setAuthStatus({ authenticated: false });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        color: 'white',
        fontSize: '18px'
      }}>
        Checking authentication...
      </div>
    );
  }

  if (!authStatus?.authenticated) {
    // Redirect to login page with return URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && authStatus.user?.role !== requiredRole) {
    // User doesn't have required role
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        color: 'white',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <h1>Access Denied</h1>
        <p>You don't have permission to access this page.</p>
        <p>Required role: {requiredRole}</p>
        <p>Your role: {authStatus.user?.role}</p>
      </div>
    );
  }

  return children;
};
